import { useLayoutEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { appCopy, normalizePhone } from '@zibanu/shared';
import { useDeps } from '../app/deps';
import { loginFlow } from '../app/loginFlow';
import { takePredrawn } from '../app/predrawn';
import { LOGIN_NUMBER_ID, LoginForm } from './LoginForm';

/** A number the flow already holds, as the field shows it: after +234, or whole with its own country code. */
const asTyped = (phone: string | null): string => (!phone ? '' : phone.startsWith('234') ? phone.slice(3) : `+${phone}`);

/** "Open your books": a number in any form people type, then a code over WhatsApp (spec 3A §7.9). */
export function Login() {
  const { client } = useDeps();
  const navigate = useNavigate();
  // Back from the code screen to change the number: it's still there to fix.
  const [typed, setTyped] = useState(() => asTyped(loginFlow.get()));
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  // What was typed into the pre-drawn login.html before the app took over (issue #8):
  // after the first commit, before the browser paints, so the field never shows empty.
  useLayoutEffect(() => {
    const early = takePredrawn();
    if (!early) return;
    if (early.number) setTyped(early.number);
    if (early.focused) document.getElementById(LOGIN_NUMBER_ID)?.focus();
  }, []);

  const submit = async () => {
    const phone = normalizePhone(typed);
    if (!phone) {
      setError(appCopy.loginBadNumber());
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const res = await client.auth.requestCode(phone);
      // The same answer whether or not the number uses Zibanu (2A): only "too many from here" is said.
      if (!res.ok && res.error === 'rate_limited') {
        setError(appCopy.loginBusy());
        return;
      }
      if (!res.ok && res.error === 'invalid_input') {
        setError(appCopy.loginBadNumber());
        return;
      }
      loginFlow.set(phone);
      await navigate({ to: '/login/code' });
    } catch {
      setError(appCopy.ourError());
    } finally {
      setBusy(false);
    }
  };

  return <LoginForm value={typed} onChange={setTyped} onSubmit={() => void submit()} error={error} busy={busy} ended={loginFlow.ended()} />;
}
